import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/lib/components/Table";
import { IconArrowNarrowDown } from "@tabler/icons-react";
import { useState } from "react";
import { TeamPermissions } from "@repo/api/team-roles";

import { useCurrentTeamMembership } from "@/lib/hooks/team-hooks";
import { useTeamMembersQuery } from "@/lib/queries/team-queries";
import InvitedMembers from "./InvitedMembers";
import ActiveMembers from "./ActiveMembers";
import InviteMembersDialog, {
  InviteFieldArrayActions,
} from "./InviteMembersDialog";
import { useIsMobile } from "@/lib/use-mobile";
import LoadingSpinner from "@/lib/components/LoadingSpinner";

type TeamTableProps = {
  className?: string;
} & InviteFieldArrayActions;

export type StatusType = "Active" | "Invited";

function TeamTable({ fields, append, remove, className }: TeamTableProps) {
  const [sortBy, setSortBy] = useState<StatusType>("Active");
  const isMobile = useIsMobile();

  const teamMembership = useCurrentTeamMembership();
  const teamExternalId = teamMembership?.teamExternalId;
  const { data, isLoading } = useTeamMembersQuery(teamExternalId!);

  if (!teamMembership || !teamExternalId) {
    return null;
  }

  const userIsAllowedToEdit = teamMembership.permissions.includes(
    TeamPermissions.TEAM_EDIT,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  return (
    <>
      {isMobile ? (
        <div className="mt-6 flex flex-col gap-4 px-4 md:px-6">
          <ActiveMembers
            isMobile={true}
            teamExternalId={teamExternalId}
            members={data?.members || []}
            userIsAllowedToEdit={userIsAllowedToEdit}
          />
          <InvitedMembers
            isMobile={true}
            teamExternalId={teamExternalId}
            invites={data?.invites || []}
            userIsAllowedToEdit={userIsAllowedToEdit}
          />
        </div>
      ) : (
        <Table className={className}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-full">Name</TableHead>
              <TableHead>
                <button
                  className="flex items-center gap-1 text-text-secondary"
                  onClick={() =>
                    setSortBy(sortBy === "Active" ? "Invited" : "Active")
                  }
                >
                  Status
                  <IconArrowNarrowDown
                    size={20}
                    className={`transition-transform ${
                      sortBy === "Active" ? "" : "rotate-180"
                    }`}
                  />
                </button>
              </TableHead>
              <TableHead>Role</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortBy === "Active" ? (
              <>
                <ActiveMembers
                  teamExternalId={teamExternalId}
                  members={data?.members || []}
                  userIsAllowedToEdit={userIsAllowedToEdit}
                />
                <InvitedMembers
                  teamExternalId={teamExternalId}
                  invites={data?.invites || []}
                  userIsAllowedToEdit={userIsAllowedToEdit}
                />
              </>
            ) : (
              <>
                <InvitedMembers
                  teamExternalId={teamExternalId}
                  invites={data?.invites || []}
                  userIsAllowedToEdit={userIsAllowedToEdit}
                />
                <ActiveMembers
                  teamExternalId={teamExternalId}
                  members={data?.members || []}
                  userIsAllowedToEdit={userIsAllowedToEdit}
                />
              </>
            )}
          </TableBody>
        </Table>
      )}
      {userIsAllowedToEdit ? (
        <InviteMembersDialog
          fields={fields}
          append={append}
          remove={remove}
          buttonVariant="ghost"
          buttonClassName="mx-auto mt-3"
        />
      ) : null}
    </>
  );
}

export default TeamTable;
